import React from 'react'

import { BaseSVGProps } from '@ui/components/icons/BaseSVG'
import { Spinner } from '@ui/components/loaders'
import clsx from 'clsx'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
  variant?: 'primary' | 'primary-red' | 'secondary' | 'tertiary' | 'danger'
  size?: 'small' | 'medium' | 'large'
  onClick?: () => void
  pill?: boolean
  icon?: React.ReactElement<BaseSVGProps> | null
  iconPosition?: 'left' | 'right'
  iconOnly?: boolean
  disabled?: boolean
  loading?: boolean
}

export const Button = ({
  children,
  variant = 'primary-red',
  size = 'small',
  pill = false,
  icon = null,
  disabled = false,
  iconPosition = 'left',
  iconOnly = false,
  loading = false,
  className,
  ...props
}: ButtonProps) => {
  const buttonDefault = 'flex items-center justify-center px-4 py-2 rounded transition-all duration-300 ease-in-out'

  const variants = {
    primary:
      'bg-gray-900 text-white hover:bg-gray-800 focus:ring focus:ring-gray-200 active:bg-gray-700 disabled:opacity-60',
    'primary-red':
      'bg-primary-500 text-white hover:bg-primary-600 focus:ring focus:ring-gray-200 active:bg-primary-700 disabled:opacity-60',
    secondary:
      'bg-transparent text-gray-900 border border-1 border-gray-900 hover:border-gray-800 focus:ring focus:ring-gray-200 active:bg-gray-100 active:text-gray-900',
    tertiary:
      'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring focus:ring-gray-200 active:bg-gray-400 active:text-gray-900',
    danger:
      'bg-negative-500 text-white hover:bg-negative-600 focus:ring focus:ring-gray-200 active:bg-negative-700 disabled:opacity-60',
  }[variant]

  const sizes = {
    small: 'text-xs font-medium',
    medium: 'text-md font-medium',
    large: 'text-lg font-medium',
  }[size]

  // TODO: Need work to fix icon sizing with custom icons
  // const iconSizes = {
  //   small: { width: 16, height: 16 },
  //   medium: { width: 20, height: 20 },
  //   large: { width: 24, height: 24 },
  // }[size]

  const iconColors = {
    primary: 'fill-[#FFF]',
    'primary-red': 'fill-[#FFF]',
    secondary: 'fill-[#FFF]',
    tertiary: 'fill-[#FFF]',
    danger: 'fill-[#FFF]',
  }[variant]

  const isPill = pill ? 'rounded-full' : ''

  return (
    <button
      disabled={disabled}
      className={clsx(buttonDefault, variants, sizes, isPill, className)}
      type={props.type || 'button'}
      {...props}>
      {iconOnly && icon ? (
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center">{loading && <Spinner />}</div>
          {React.cloneElement(icon, { color: iconColors })}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          {icon && iconPosition === 'left' && <span>{React.cloneElement(icon, { color: iconColors })}</span>}
          {loading && <Spinner />}
          {children}
          {icon && iconPosition === 'right' && <span>{React.cloneElement(icon, { color: iconColors })}</span>}
        </div>
      )}
    </button>
  )
}
